import { DI_TOKENS, initializeDIContainer, resolve } from '@src/di/container';
import { ServiceContainerProvider } from '@src/services/ServiceContainerContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import React, { createContext, useContext, useEffect } from 'react';

import { useStore } from './StoreContext';

import type { IVideoCreatorStore } from '../stores/IVideoCreatorStore';
import type VideoCreatorStore from '../stores/VideoCreatorStore';
import type { ReactNode } from 'react';

export const VideoCreatorStoreContext =
  createContext<IVideoCreatorStore | null>(null);

interface VideoCreatorStoreProviderProps {
  children: ReactNode;
}

export function VideoCreatorStoreProvider({
  children,
}: VideoCreatorStoreProviderProps) {
  const rootStore = useStore();
  const flags = useFlagsCombination(rootStore.userIdentityStore.currentRole);

  // Initialize DI container and resolve VideoCreatorStore + ServiceContainer.
  // The ServiceContainer is created as a side effect of initializeDIContainer
  // (when one isn't supplied) and shared with React via ServiceContainerProvider.
  const { videoCreatorStore, serviceContainer } = React.useMemo(() => {
    console.log(
      'VideoCreatorStoreProvider rootStore changed - initializeDIContainer',
    );
    initializeDIContainer(rootStore);

    return {
      videoCreatorStore: resolve(DI_TOKENS.VIDEO_CREATOR_STORE),
      serviceContainer: resolve(DI_TOKENS.SERVICE_CONTAINER),
    };
  }, [rootStore]);

  useEffect(() => {
    if (flags.initialized) {
      videoCreatorStore.setFlags(flags);
      rootStore.analyticsStore.setPostHogAIObservabilityEnabled(
        !!flags.enablePostHogAiObservability,
      );
    }
  }, [videoCreatorStore, flags]);

  return (
    <ServiceContainerProvider container={serviceContainer}>
      <VideoCreatorStoreContext.Provider value={videoCreatorStore}>
        {children}
      </VideoCreatorStoreContext.Provider>
    </ServiceContainerProvider>
  );
}

export const useVideoCreatorStore = () => {
  const context = useContext(VideoCreatorStoreContext);
  if (!context) {
    throw new Error(
      'useVideoCreatorStore must be used within a VideoCreatorStoreProvider',
    );
  }
  return context;
};

// Helper type for components that need VideoCreatorStore
export type WithVideoCreatorStore<P = {}> = P & {
  videoCreatorStore?: VideoCreatorStore;
};
